import { Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  AssistantKey,
  SuccessEvaluatorStat,
  TestCase,
  TestResult,
  TestType,
  deleteTestCases$,
  getSuccessEvaluatorStat$,
  getTestResult$,
  listSuccessEvaluatorResults$,
  listSuccessEvaluatorStatsForAssistant$,
  upsertTestCases$,
} from '@vendasta/protos/ai_assistants/v1alpha1';
import { isNotFound, isResourceExhausted } from '../../core/utils/grpc-status.util';

export interface JobStat {
  testCase: TestCase;
  // Fraction of evaluated results that passed, 0-1. Null when nothing has been
  // evaluated against the job yet, which reads differently from a rate of 0.
  successRate: number | null;
  // The rate the job is held to, 0-1. Resolved by the server, so it is set even
  // for a job that carries no target of its own.
  targetRate: number;
  // True for a job defined against this assistant rather than one of the shared
  // Global ones.
  custom: boolean;
  // How many scored results the rate was computed from; 0 when the job has never
  // been evaluated.
  evaluatedCount: number;
  // How many of those scored results passed.
  passedCount: number;
  // Length of the trailing window the rate covers, in days. Server-defined.
  evaluationWindowDays: number;
}

/** The job a user wrote, as the card collects it. */
export interface JobDraft {
  /** Empty when creating; the existing job's id when editing one. */
  id?: string;
  name: string;
  scenario: string;
  expectation: string;
  /** The rate to hold the job to, 0-1. */
  successEvaluationTargetRate: number;
}

/** One page of a single evaluator's scored conversations. */
export interface EvaluatorResultsPage {
  results: TestResult[];
  // Pass back to fetch the following page; empty when there is none.
  nextCursor: string;
  hasMore: boolean;
}

/**
 * A few rows to open on, so the modal settles quickly — every result carries a
 * full transcript, so the first page is the one the user waits for.
 */
export const INITIAL_EVALUATOR_RESULTS_PAGE_SIZE = 3;

/** Pages after the first, once the user has asked for more. */
export const EVALUATOR_RESULTS_PAGE_SIZE = 25;

/**
 * Page size retried after a response exceeds the gRPC message limit. Matches the
 * size the test-results path settled on for the same payload type.
 */
const EVALUATOR_RESULTS_FALLBACK_PAGE_SIZE = 5;

/**
 * Query params that make a job and one of its scored conversations addressable. The
 * card is embedded in a page rather than routed to, so its state rides on whatever
 * route is showing it.
 */
export const JOB_QUERY_PARAM = 'job';
export const RESULT_QUERY_PARAM = 'result';

/**
 * Writes query params onto the current URL, leaving the path alone. Empty commands
 * keep the route the card happens to be mounted on, so no caller needs to know it.
 * A null value drops the param; the write replaces rather than stacking history.
 */
export function mergeQueryParams(router: Router, queryParams: Params): void {
  void router.navigate([], { queryParams, queryParamsHandling: 'merge', replaceUrl: true });
}

/**
 * Whether the job is defined against the assistant rather than shared. The response
 * carries only Global jobs and the assistant's own, so anything outside the Global
 * namespace is the assistant's. A job whose namespace is missing is treated as
 * shared, so a gap in the data cannot mislabel a built-in job as the user's.
 */
function isAssistantOwned(testCase: TestCase): boolean {
  const namespace = testCase.assistantKey?.namespace;
  return !!namespace && !namespace.globalNamespace;
}

/** One row as the card reads it, from the stat the endpoint reports. */
function toJobStat(stat: SuccessEvaluatorStat): JobStat {
  const testCase = stat.testCase as TestCase;
  return {
    testCase,
    // A zero evaluated count means nothing has been scored against the job yet.
    // Reporting that as 0% would read as every conversation failing it.
    successRate: stat.evaluatedCount > 0 ? stat.successRate : null,
    targetRate: stat.targetRate,
    custom: isAssistantOwned(testCase),
    evaluatedCount: stat.evaluatedCount,
    passedCount: Math.round(stat.successRate * stat.evaluatedCount),
    evaluationWindowDays: stat.evaluationWindowDays,
  };
}

@Injectable({
  providedIn: 'root',
})
export class JobsToBeDoneService {
  listJobsToBeDone(assistantKey: Partial<AssistantKey>): Observable<JobStat[]> {
    return listSuccessEvaluatorStatsForAssistant$({ assistant: assistantKey }).pipe(
      map((response) =>
        response.evaluators.filter((stat) => stat.testCase !== undefined).map((stat) => toJobStat(stat)),
      ),
    );
  }

  /**
   * Fetches one page of the conversations a single evaluator scored, most
   * recently evaluated first. Only scored conversations are returned, so the page
   * covers the same set the evaluator's success rate is computed from. Pass the
   * previous page's nextCursor to continue.
   *
   * A page may come back smaller than asked for: an over-limit response is retried
   * at a smaller size rather than failed.
   */
  listEvaluatorResults(
    assistantKey: Partial<AssistantKey>,
    testCaseId: string,
    cursor = '',
  ): Observable<EvaluatorResultsPage> {
    const pageSize = cursor ? EVALUATOR_RESULTS_PAGE_SIZE : INITIAL_EVALUATOR_RESULTS_PAGE_SIZE;
    return this.requestPage(assistantKey, testCaseId, cursor, pageSize).pipe(
      // Response size follows conversation length, not row count, so a page can
      // exceed the gRPC message limit at any size. Retrying smaller keeps the page
      // usable; the first page is already below the fallback and is not retried.
      catchError((err) => {
        if (pageSize > EVALUATOR_RESULTS_FALLBACK_PAGE_SIZE && isResourceExhausted(err)) {
          return this.requestPage(assistantKey, testCaseId, cursor, EVALUATOR_RESULTS_FALLBACK_PAGE_SIZE);
        }
        return throwError(() => err);
      }),
    );
  }

  /**
   * The job a link names, or null when it is not one this assistant is reported on.
   * Read by id rather than found in the list, so a link does not depend on what the
   * list happened to return.
   */
  /**
   * Removes a job the assistant owns. The results scored against it are not
   * removed with it — they stay under their test runs, unreachable from the card.
   */
  deleteJob(assistantKey: Partial<AssistantKey>, testCaseId: string): Observable<void> {
    return deleteTestCases$({ assistant: assistantKey, testCaseIds: [testCaseId] });
  }

  getJob(assistantKey: Partial<AssistantKey>, testCaseId: string): Observable<JobStat | null> {
    return getSuccessEvaluatorStat$({ assistant: assistantKey, testCaseId }).pipe(
      map((response) => {
        const stat = response.evaluator;
        if (!stat?.testCase) return null;
        return toJobStat(stat);
      }),
      catchError((err) => (isNotFound(err) ? of(null) : throwError(() => err))),
    );
  }

  /**
   * The one scored conversation a link names, or null when the run holds no result
   * for this job. Iteration 1 is the only one success evaluation produces.
   */
  getEvaluatorResult(
    assistantKey: Partial<AssistantKey>,
    testCaseId: string,
    testRunId: string,
  ): Observable<TestResult | null> {
    return getTestResult$({
      assistant: assistantKey,
      testRunId,
      testCaseId,
      iterationNumber: 1,
    }).pipe(
      map((response) => response.testResult ?? null),
      catchError((err) => (isNotFound(err) ? of(null) : throwError(() => err))),
    );
  }

  private requestPage(
    assistantKey: Partial<AssistantKey>,
    testCaseId: string,
    cursor: string,
    pageSize: number,
  ): Observable<EvaluatorResultsPage> {
    return listSuccessEvaluatorResults$({
      assistant: assistantKey,
      testCaseId,
      pagingOptions: { pageSize, cursor },
    }).pipe(
      map((response) => ({
        results: response.testResults ?? [],
        nextCursor: response.pagingMetadata?.nextCursor ?? '',
        hasMore: response.pagingMetadata?.hasMore ?? false,
      })),
    );
  }

  /**
   * Creates or updates one of the assistant's own jobs. `testType` fixes the
   * modality the job is scored on — an assistant is evaluated on one medium, and
   * a job of the wrong type is never matched against its conversations.
   *
   * The job is a grading rubric, not a scripted test: it carries no conversation,
   * and no constraints, which scores it against every conversation of its type.
   */
  saveJob(assistantKey: Partial<AssistantKey>, draft: JobDraft, testType: TestType): Observable<void> {
    return upsertTestCases$({
      assistant: assistantKey,
      testCases: [
        {
          id: draft.id ?? '',
          assistantKey,
          name: draft.name,
          scenario: draft.scenario,
          expectation: draft.expectation,
          successEvaluationTargetRate: draft.successEvaluationTargetRate,
          isSuccessEvaluator: true,
          testType,
        },
      ],
    });
  }
}
